import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import { supabase } from "../supabaseClient";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authStatus, setAuthStatus] = useState("initializing");
  const [sessionError, setSessionError] = useState(null);

  const currentUserIdRef = useRef(null);

  // Derive a single "primary" role for backward compatibility.
  // Defined before loadUserProfile so it can be referenced inside useCallback.
  const deriveRole = useCallback((roles) => {
    if (roles.includes("admin")) return "admin";
    if (roles.includes("scorekeeper")) return "scorekeeper";
    if (roles.includes("team_captain")) return "team_captain";
    if (roles.includes("player")) return "player";
    return null;
  }, []);

  // Wrapped in useCallback so the stable reference can be listed as a
  // dependency of the auth-state useEffect without triggering re-runs.
  // Dependencies: supabase is module-level (never changes); currentUserIdRef
  // is a ref (stable object); deriveRole is memoised above.
  const loadUserProfile = useCallback(
    (userId) => {
      setTimeout(async () => {
        setAuthStatus("loading");

        try {
          // Fetch profile and all roles in parallel
          const [
            { data: profile, error: profileError },
            { data: rolesData, error: rolesError },
          ] = await Promise.all([
            supabase
              .from("profiles")
              .select(
                "id, email, name, team_id, must_change_password, avatar_url",
              )
              .eq("id", userId)
              .maybeSingle(),
            supabase
              .from("profile_roles")
              .select("role, team_id, season_id")
              .eq("profile_id", userId),
          ]);

          if (profileError) throw profileError;
          if (!profile) throw new Error(`No profile found for user ${userId}`);
          if (rolesError) throw rolesError;

          const roles = (rolesData || []).map((r) => r.role);

          currentUserIdRef.current = userId;
          setUser({
            id: userId,
            email: profile.email,
            name: profile.name,
            // Keep legacy role field for any code that still reads it directly,
            // derived as the "primary" role (admin > scorekeeper > team_captain > player)
            role: deriveRole(roles),
            // New: full roles array and scoped role entries
            roles,
            roleEntries: rolesData || [],
            team_id: profile.team_id,
            mustChangePassword: profile.must_change_password ?? false,
            avatar_url: profile.avatar_url,
          });
          setSessionError(null);
          setAuthStatus("idle");
        } catch (err) {
          currentUserIdRef.current = null;
          setUser(null);
          setAuthStatus("idle");
          setSessionError(
            "Your account is still being set up. Please wait a moment and refresh.",
          );
        }
      }, 0);
    },
    [deriveRole],
  );

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      const isProcessingMagicLink =
        window.location.hash.includes("access_token=");

      switch (event) {
        case "INITIAL_SESSION":
          if (session?.user?.id) {
            if (currentUserIdRef.current === session.user.id) break;
            await loadUserProfile(session.user.id);
          } else {
            if (!isProcessingMagicLink) {
              currentUserIdRef.current = null;
              setUser(null);
              setSessionError(null);
              setAuthStatus("idle");
            }
          }
          break;

        case "SIGNED_IN":
          if (!session?.user) {
            await supabase.auth.signOut();
            setUser(null);
            currentUserIdRef.current = null;
            setAuthStatus("idle");
            break;
          }
          if (currentUserIdRef.current === session.user.id) break;
          await loadUserProfile(session.user.id);
          break;

        case "TOKEN_REFRESHED":
          break;

        case "SIGNED_OUT":
        case "USER_DELETED":
          currentUserIdRef.current = null;
          setUser(null);
          setSessionError(null);
          setAuthStatus("idle");
          break;

        default:
          if (session?.user) {
            await loadUserProfile(session.user.id);
          }
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, [loadUserProfile]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (authStatus === "initializing" || authStatus === "loading") {
        setAuthStatus("idle");
        setSessionError("Session took too long to load. Please refresh.");
      }
    }, 4000);
    return () => clearTimeout(timeout);
  }, [authStatus]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session && user) {
        currentUserIdRef.current = null;
        setUser(null);
        setAuthStatus("idle");
        setSessionError("Your session expired. Please log in again.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user]);

  const signIn = async (email, password) => {
    setSessionError(null);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPasswordForEmail = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  // Used for both the "forgot password" reset flow and the forced first-login
  // change for player accounts created with a temp password.
  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;

    if (currentUserIdRef.current) {
      await supabase
        .from("profiles")
        .update({ must_change_password: false })
        .eq("id", currentUserIdRef.current);
      setUser((prev) => (prev ? { ...prev, mustChangePassword: false } : prev));
    }
  };

  // Helper: check if user has a specific role (any scope)
  const hasRole = (role) => user?.roles?.includes(role) ?? false;

  // Helper: check if user is captain for a specific team
  const isCaptainOfTeam = (teamId) =>
    user?.roleEntries?.some(
      (r) => r.role === "team_captain" && r.team_id === teamId,
    ) ?? false;

  const value = {
    user,
    authStatus,
    loading: authStatus !== "idle",
    sessionError,
    signIn,
    signOut,
    resetPasswordForEmail,
    updatePassword,
    hasRole,
    isCaptainOfTeam,
    // Convenience booleans — true if user has that role in ANY scope
    isAdmin: hasRole("admin"),
    isCaptain: hasRole("team_captain"),
    isScorekeeper: hasRole("scorekeeper"),
    isPlayer: hasRole("player"),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
